export type DashboardDeltaDirection = 'up' | 'down' | 'neutral'
export type DashboardDeltaIntent = 'positive' | 'negative' | 'neutral'

export interface DashboardKpi {
  key: string
  iconKey:
    | 'billed'
    | 'clean_claim'
    | 'open_ar'
    | 'days_to_pay'
    | 'denial_rate'
    | 'pa_approval'
  label: string
  value: string
  sublabel: string
  delta: {
    text: string
    direction: DashboardDeltaDirection
    intent: DashboardDeltaIntent
  }
}

export interface MonthlyBilledRow {
  month: string
  billed: number
  collected: number
}

export type AlertActionIntent = 'critical' | 'warning' | 'info' | 'neutral'
export type AlertActionIconKey = 'denied' | 'eligibility' | 'prior_auth' | 'era' | 'contract'

export interface AlertActionItem {
  id: string
  iconKey: AlertActionIconKey
  intent: AlertActionIntent
  title: string
  subtitle: string
  actionLabel: string
}

export interface ArAgingBucket {
  label: string
  amount: number
  toneClass: string
}

export interface PayorMixSlice {
  label: string
  percent: number
  colorClass: string
  bulletClass: string
}

export type RecentClaimStatus = 'Paid' | 'Pending' | 'Denied' | 'Submitted'

export interface RecentClaim {
  id: string
  claim: string
  patient: string
  payor: string
  amount: number
  status: RecentClaimStatus
}

export interface DashboardData {
  greetingDate: string
  alertsCount: number
  kpis: DashboardKpi[]
  monthly: MonthlyBilledRow[]
  alerts: AlertActionItem[]
  arAging: ArAgingBucket[]
  arTotal: number
  payorMix: PayorMixSlice[]
  recentClaims: RecentClaim[]
}

export const dashboardMock: DashboardData = {
  greetingDate: 'May 21, 2026',
  alertsCount: 3,
  kpis: [
    {
      key: 'ytd_billed',
      iconKey: 'billed',
      label: 'YTD billed',
      value: '$1,842,650',
      sublabel: 'vs $1,700,180 last year',
      delta: { text: '8.4%', direction: 'up', intent: 'positive' },
    },
    {
      key: 'clean_claim',
      iconKey: 'clean_claim',
      label: 'Clean claim rate',
      value: '94.2%',
      sublabel: 'Target: 95%',
      delta: { text: '1.2%', direction: 'up', intent: 'positive' },
    },
    {
      key: 'open_ar',
      iconKey: 'open_ar',
      label: 'Open AR',
      value: '$218,440',
      sublabel: '$48,230 patient responsibility',
      delta: { text: '3.1%', direction: 'down', intent: 'positive' },
    },
    {
      key: 'days_to_pay',
      iconKey: 'days_to_pay',
      label: 'Avg. days to pay',
      value: '18 days',
      sublabel: 'Payor average: 21 days',
      delta: { text: '+2 days', direction: 'up', intent: 'negative' },
    },
    {
      key: 'denial_rate',
      iconKey: 'denial_rate',
      label: 'Denial rate',
      value: '5.8%',
      sublabel: '47 claims this month',
      delta: { text: '0.8%', direction: 'down', intent: 'positive' },
    },
    {
      key: 'pa_approval',
      iconKey: 'pa_approval',
      label: 'PA approval rate',
      value: '83%',
      sublabel: '64 of 77 approved MTD',
      delta: { text: '12 pending', direction: 'neutral', intent: 'neutral' },
    },
  ],
  monthly: [
    { month: 'Dec', billed: 285000, collected: 232000 },
    { month: 'Jan', billed: 268000, collected: 218000 },
    { month: 'Feb', billed: 304000, collected: 268000 },
    { month: 'Mar', billed: 322000, collected: 285000 },
    { month: 'Apr', billed: 312000, collected: 278000 },
    { month: 'May', billed: 351000, collected: 309000 },
  ],
  alerts: [
    {
      id: 'alert-1',
      iconKey: 'denied',
      intent: 'critical',
      title: '4 claims denied — action required',
      subtitle: 'BlueCross BlueShield TX · Denial code CO-4',
      actionLabel: 'Review',
    },
    {
      id: 'alert-2',
      iconKey: 'eligibility',
      intent: 'warning',
      title: '7 eligibility checks expiring within 14 days',
      subtitle: 'Anil Patel, Marcus Webb +5 others',
      actionLabel: 'Verify',
    },
    {
      id: 'alert-3',
      iconKey: 'prior_auth',
      intent: 'warning',
      title: '12 prior auth renewals pending',
      subtitle: 'Oxygen equipment, CPAP — due within 30 days',
      actionLabel: 'View',
    },
    {
      id: 'alert-4',
      iconKey: 'era',
      intent: 'info',
      title: 'ERA batch ready to post',
      subtitle: '$31,450 from BCBS TX · received 05/20/2026',
      actionLabel: 'Post',
    },
    {
      id: 'alert-5',
      iconKey: 'contract',
      intent: 'neutral',
      title: 'Contract renewal — BCBS TX',
      subtitle: 'Expires 12/31/2025 — begin renegotiation',
      actionLabel: 'Open',
    },
  ],
  arAging: [
    { label: '0–30 days', amount: 139802, toneClass: 'bg-green-600' },
    { label: '31–60 days', amount: 48057, toneClass: 'bg-orange-500' },
    { label: '61–90 days', amount: 26232, toneClass: 'bg-amber-500' },
    { label: '90+ days', amount: 4349, toneClass: 'bg-rose-600' },
  ],
  arTotal: 218440,
  payorMix: [
    {
      label: 'BlueCross BlueShield TX',
      percent: 38,
      colorClass: 'bg-blue-500',
      bulletClass: 'bg-blue-500',
    },
    {
      label: 'UnitedHealthcare',
      percent: 24,
      colorClass: 'bg-emerald-500',
      bulletClass: 'bg-emerald-500',
    },
    {
      label: 'Aetna Texas',
      percent: 18,
      colorClass: 'bg-violet-500',
      bulletClass: 'bg-violet-500',
    },
    {
      label: 'Cigna Health',
      percent: 11,
      colorClass: 'bg-amber-600',
      bulletClass: 'bg-amber-600',
    },
    {
      label: 'All others',
      percent: 9,
      colorClass: 'bg-gray-400',
      bulletClass: 'bg-gray-400',
    },
  ],
  recentClaims: [
    {
      id: 'rcl-9841',
      claim: 'CLM-9841',
      patient: 'J. Martinez',
      payor: 'BCBS TX',
      amount: 284.4,
      status: 'Paid',
    },
    {
      id: 'rcl-9840',
      claim: 'CLM-9840',
      patient: 'S. Nguyen',
      payor: 'Cigna',
      amount: 142.8,
      status: 'Pending',
    },
    {
      id: 'rcl-9839',
      claim: 'CLM-9839',
      patient: 'R. Thompson',
      payor: 'BCBS TX',
      amount: 420.0,
      status: 'Paid',
    },
    {
      id: 'rcl-9838',
      claim: 'CLM-9838',
      patient: 'D. Liu',
      payor: 'Aetna',
      amount: 190.5,
      status: 'Denied',
    },
    {
      id: 'rcl-9837',
      claim: 'CLM-9837',
      patient: 'M. Webb',
      payor: 'UHC',
      amount: 355.2,
      status: 'Pending',
    },
  ],
}
